import type { BrickId } from './brick.js';
import type { Mm } from './units.js';

/**
 * How a snapped position relates to what it snapped to (clause 6.2).
 * `left`/`right` are end (perpend) joints, `above`/`below` are bed joints,
 * and the remaining three are datum snaps against the setting-out axes.
 */
export type SnapRelation =
  | 'left' | 'right' | 'above' | 'below'
  | 'axes' | 'y-axis' | 'baseline'
  /** An edge landed on a drawn sketch line. */
  | 'sketch';

export interface SnapResult {
  readonly x: Mm;
  readonly y: Mm;
  readonly relation: SnapRelation | null;
  readonly referenceId: BrickId | null;
  readonly snappedX: boolean;
  readonly snappedY: boolean;
}

export function unsnapped(x: Mm, y: Mm): SnapResult {
  return { x, y, relation: null, referenceId: null, snappedX: false, snappedY: false };
}

/**
 * Priority tiers, lowest wins (clause 6.4).
 *
 * Sketch lines sit between the joints and the setting-out axes: a line the user
 * drew is a stronger intention than the global datums, and a weaker one than a
 * joint against real brickwork.
 */
export const enum SnapPriority {
  EndJoint = 0,
  BedJoint = 1,
  SketchLine = 2,
  BothAxes = 3,
  OneAxis = 4,
}

export interface SnapCandidate {
  readonly x: Mm;
  readonly y: Mm;
  readonly distance: number;
  readonly priority: SnapPriority;
  readonly order: number;
  readonly relation: SnapRelation;
  readonly referenceId: BrickId | null;
  readonly snappedX: boolean;
  readonly snappedY: boolean;
}

/**
 * Sort by the four-part key from clause 6.4: priority, then whether both axes
 * were pinned, then distance from the raw position rounded to 9 decimals so
 * float noise cannot reorder equals, then generation order as a stable
 * tie-break.
 */
export function compareCandidates(a: SnapCandidate, b: SnapCandidate): number {
  if (a.priority !== b.priority) return a.priority - b.priority;
  const bothA = a.snappedX && a.snappedY ? 0 : 1;
  const bothB = b.snappedX && b.snappedY ? 0 : 1;
  if (bothA !== bothB) return bothA - bothB;
  const da = Math.round(a.distance * 1e9) / 1e9;
  const db = Math.round(b.distance * 1e9) / 1e9;
  if (da !== db) return da - db;
  return a.order - b.order;
}


/**
 * Collects candidates, keeping only the best-ranked one per position.
 *
 * A regular bond makes every member of a large pattern propose the SAME few
 * origins, so a copied wall can generate thousands of candidates that name a
 * handful of distinct placements. Validation is the expensive half, and it
 * depends only on the position — so collapsing duplicates is free of any
 * behavioural change and is what keeps a paste preview interactive.
 */
export class SnapCandidateSet {
  #best = new Map<string, SnapCandidate>();

  add(candidate: SnapCandidate): void {
    const key = `${candidate.x},${candidate.y}`;
    const existing = this.#best.get(key);
    if (existing === undefined || compareCandidates(candidate, existing) < 0) {
      this.#best.set(key, candidate);
    }
  }

  get size(): number {
    return this.#best.size;
  }

  sorted(): SnapCandidate[] {
    return [...this.#best.values()].sort(compareCandidates);
  }
}
